export interface BootstrapAttribution {
  domainPid: string;
  fbPixelId: string;
  countryCode?: string;
  emAppKey?: string;
}

export interface RuntimeAttribution {
  restoredPid?: string;
}

export interface AttributionWindowLike {
  __BOOTSTRAP_ATTR__?: Partial<BootstrapAttribution>;
  __RUNTIME_ATTR__?: RuntimeAttribution;
}

const getDefaultWindow = (): AttributionWindowLike | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window;
};

const getSafeWindow = (
  target?: AttributionWindowLike,
): AttributionWindowLike | undefined => {
  return target ?? getDefaultWindow();
};

const getRuntimeAttribution = (
  target?: AttributionWindowLike,
): RuntimeAttribution => {
  const win = getSafeWindow(target);
  if (!win) {
    return {};
  }

  if (!win.__RUNTIME_ATTR__) {
    win.__RUNTIME_ATTR__ = {};
  }

  return win.__RUNTIME_ATTR__;
};

export const getBootstrapAttribution = (
  target?: AttributionWindowLike,
): BootstrapAttribution => {
  const win = getSafeWindow(target);
  const bootstrap = win?.__BOOTSTRAP_ATTR__;

  return {
    domainPid:
      typeof bootstrap?.domainPid === "string" ? bootstrap.domainPid : "",
    fbPixelId:
      typeof bootstrap?.fbPixelId === "string" ? bootstrap.fbPixelId : "",
    countryCode:
      typeof bootstrap?.countryCode === "string"
        ? bootstrap.countryCode
        : undefined,
    ...(typeof bootstrap?.emAppKey === "string"
      ? { emAppKey: bootstrap.emAppKey }
      : {}),
  };
};

export const setRestoredPid = (
  restoredPid?: string,
  target?: AttributionWindowLike,
): void => {
  const runtime = getRuntimeAttribution(target);
  const normalizedPid = restoredPid?.trim();

  if (normalizedPid) {
    runtime.restoredPid = normalizedPid;
    return;
  }

  delete runtime.restoredPid;
};

export const getResolvedPid = (target?: AttributionWindowLike): string => {
  const runtime = getRuntimeAttribution(target);
  const restoredPid = runtime.restoredPid?.trim();
  if (restoredPid) {
    return restoredPid;
  }

  return getBootstrapAttribution(target).domainPid;
};

export const getResolvedFbPixelId = (
  target?: AttributionWindowLike,
): string => {
  return getBootstrapAttribution(target).fbPixelId;
};
